import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { WEB_MAX_CONTENT_WIDTH } from '../../theme/webLayoutWidths';

import {
  FlatList,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';
import { isTablet } from 'react-native-device-info';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import { ActivityIndicator } from 'react-native-paper';
import { useIsFocused, useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { GTFonts, useLayoutSize, useTheme } from '../../theme';
import { fs, iconSize, s, v } from '../../utils/libs';
import { shouldShowTabletSidebarLayoutSetting } from '../../utils/layoutUtils';
import { formatDisplayDateTime } from '../../utils/formatDisplayDateTime';
import AppSearchBar from '../../components/Reusables/AppSearchBar';
import AppSearchBarWeb from '../../components/Reusables/AppSearchBar.web';
import WebHeaderBar from '../../components/web/WebHeaderBar';
import useWebHeader from '../../components/web/useWebHeader';
import useBackofficeAxios from '../../hooks/useBackofficeAxios';
import useBackofficeOwnerAuth from '../../hooks/useBackofficeOwnerAuth';
import {
  listCustomers,
  parseCustomersResponse,
} from '../../api/backofficeCustomers';
import { useAppDispatch, useAppSelector } from '../../redux/helper';
import {
  appendCustomers,
  setCustomers,
  setCustomersError,
  setCustomersLoading,
  setCustomersSearchTerm,
  setSelectedCustomerId,
  type Customer,
} from '../../redux/backoffice/customersSlice';
import { selectValidCurrentLocation } from '../../redux/locationSlice';
import type { RootStackParamList } from '../../navigation/AppStack';
import { canCreateCustomer } from '../../utils/roles';

type Props = {
  embedded?: boolean;
};

const BackOfficeCustomersScreen: React.FC<Props> = ({ embedded = false }) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const isFocused = useIsFocused();
  const dispatch = useAppDispatch();
  const axiosBackoffice = useBackofficeAxios();
  const { owner } = useBackofficeOwnerAuth();
  const { useSidebarNav } = useLayoutSize();
  const { width, height } = useWindowDimensions();
  const nativeHeaderSearchWidth = isTablet()
    ? Math.max(width * 0.7, width - s(110))
    : width * 0.7;
  const showTabletSidebarLayoutSetting = shouldShowTabletSidebarLayoutSetting(
    width,
    height,
  );

  const employee = useAppSelector(
    state => state.auth.inApp.account.employeeInfo as any,
  );
  const showAdd = canCreateCustomer(employee?.bp_employee_role);
  const customers = useAppSelector(state => state.backofficeCustomers.items);
  const loading = useAppSelector(state => state.backofficeCustomers.loading);
  const page = useAppSelector(state => state.backofficeCustomers.page);
  const pageSize = useAppSelector(state => state.backofficeCustomers.pageSize);
  const hasNextPage = useAppSelector(
    state => state.backofficeCustomers.hasNextPage,
  );
  const searchTerm = useAppSelector(
    state => state.backofficeCustomers.searchTerm,
  );
  const locationVersion = useAppSelector(
    state => state.location.locationVersion,
  );
  const currentLocation = useAppSelector(selectValidCurrentLocation);
  const locationId = currentLocation?._id ?? '';

  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState(() =>
    String(searchTerm || '').trim(),
  );
  const fetchGenRef = useRef(0);
  const locationKey = `${locationId}:${locationVersion}`;
  const prevLocationKeyRef = useRef(locationKey);

  const goAddCustomer = useCallback(() => {
    navigation.navigate('BackOfficeAddCustomer');
  }, [navigation]);

  const headerRightNode = useMemo(() => {
    if (!showAdd) return null;
    return (
      <Pressable
        onPress={goAddCustomer}
        style={({ pressed, hovered }: any) => [
          styles.headerCTA,
          pressed && styles.headerCTAPressed,
          Platform.OS === 'web' && hovered && styles.headerCTAHovered,
        ]}>
        <Text style={styles.headerCTAText}>+ Add New</Text>
      </Pressable>
    );
  }, [goAddCustomer, showAdd, styles]);

  const searchBar = useMemo(
    () => (
      <View
        style={{
          width: '100%',
          maxWidth: nativeHeaderSearchWidth,
        }}>
        <AppSearchBar
          placeholder="Search customers..."
          value={searchTerm}
          onChangeText={text => dispatch(setCustomersSearchTerm(text))}
          onClear={() => dispatch(setCustomersSearchTerm(''))}
          showLeadingIcon={true}
          showBarcodeScan={false}
          width={nativeHeaderSearchWidth}
          height={v(50)}
          containerStyle={{
            backgroundColor: theme.colors.surface,
            borderColor: theme.colors.surface,
            borderWidth: 0,
          }}
        />
      </View>
    ),
    [dispatch, nativeHeaderSearchWidth, searchTerm, theme.colors.surface],
  );

  const webSearchBar = useMemo(
    () => (
      <View
        style={{
          width: '100%',
          maxWidth: width >= 768 && width < 1024 ? s(660) : s(620),
        }}>
        <AppSearchBarWeb
          placeholder="Search customers..."
          value={searchTerm}
          onChangeText={text => dispatch(setCustomersSearchTerm(text))}
          onClear={() => dispatch(setCustomersSearchTerm(''))}
          showLeadingIcon={true}
          showBarcodeScan={false}
          width={
            showTabletSidebarLayoutSetting && useSidebarNav
              ? width * 0.46
              : width * 0.32
          }
          height={v(50)}
          containerStyle={{
            backgroundColor: theme.colors.surface,
            borderColor: theme.colors.surface,
            borderWidth: 0,
          }}
        />
      </View>
    ),
    [
      dispatch,
      searchTerm,
      showTabletSidebarLayoutSetting,
      theme.colors.surface,
      useSidebarNav,
      width,
    ],
  );

  useLayoutEffect(() => {
    if (useSidebarNav) return;
    if (embedded && !isFocused) return;
    const targetNav = embedded
      ? (navigation.getParent() ?? navigation)
      : navigation;
    (targetNav as any).setOptions({
      headerTitleContainerStyle: { flex: 1 },
      headerTitle: () => searchBar,
      headerRight: () => headerRightNode,
    });
  }, [
    embedded,
    headerRightNode,
    isFocused,
    navigation,
    searchBar,
    useSidebarNav,
  ]);

  const webHeaderNode = useMemo(() => {
    if (!useSidebarNav) return null;
    return (
      <WebHeaderBar
        rightSlot={headerRightNode}
        onBack={() => {
          try {
            (navigation as any).navigate('BackOffice', {
              screen: 'Dashboard',
            });
          } catch {
            if (navigation.canGoBack()) navigation.goBack();
          }
        }}>
        {webSearchBar}
      </WebHeaderBar>
    );
  }, [headerRightNode, navigation, useSidebarNav, webSearchBar]);

  useWebHeader(
    webHeaderNode,
    Boolean(webHeaderNode) && (!embedded || isFocused),
  );

  useEffect(() => {
    if (prevLocationKeyRef.current === locationKey) return;
    prevLocationKeyRef.current = locationKey;
    dispatch(setCustomersSearchTerm(''));
    setDebouncedSearch('');
  }, [dispatch, locationKey]);

  useEffect(() => {
    const next = String(searchTerm || '').trim();
    const delay = next ? 300 : 0;
    const timer = setTimeout(() => setDebouncedSearch(next), delay);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchPage = useCallback(
    async (targetPage: number, append: boolean) => {
      if (!owner?.accessToken || !locationId) return;
      const gen = append ? fetchGenRef.current : fetchGenRef.current + 1;
      if (!append) {
        fetchGenRef.current = gen;
      }
      try {
        if (!append) {
          dispatch(setCustomersLoading(true));
        }
        const res = await listCustomers(
          axiosBackoffice as any,
          locationId,
          targetPage,
          pageSize,
          { accessToken: owner.accessToken },
          debouncedSearch,
        );
        if (gen !== fetchGenRef.current) return;
        const parsed = parseCustomersResponse(res);
        if (!parsed?.success) {
          dispatch(
            setCustomersError(parsed?.message || 'Failed to load customers'),
          );
          return;
        }
        const payload = {
          items: parsed.customers || [],
          total: parsed.total || 0,
          page: parsed.page || targetPage,
          pageSize: parsed.limit || pageSize,
        };
        if (append) {
          dispatch(appendCustomers(payload));
          return;
        }
        dispatch(setCustomers(payload));
      } catch (error: any) {
        if (gen !== fetchGenRef.current) return;
        dispatch(
          setCustomersError(error?.message || 'Failed to load customers'),
        );
      }
    },
    [
      axiosBackoffice,
      debouncedSearch,
      dispatch,
      locationId,
      owner?.accessToken,
      pageSize,
    ],
  );

  useEffect(() => {
    if (!owner?.accessToken || !locationId) return;
    fetchPage(1, false);
  }, [fetchPage, locationId, locationVersion, owner?.accessToken]);

  const handleLoadMore = useCallback(async () => {
    if (!owner?.accessToken || !locationId) return;
    if (loading || isLoadingMore || !hasNextPage) return;
    setIsLoadingMore(true);
    try {
      await fetchPage(Number(page || 1) + 1, true);
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    fetchPage,
    hasNextPage,
    isLoadingMore,
    loading,
    locationId,
    owner?.accessToken,
    page,
  ]);

  const onRefresh = useCallback(async () => {
    if (refreshing || loading || !owner?.accessToken || !locationId) return;
    setRefreshing(true);
    try {
      await fetchPage(1, false);
    } finally {
      setRefreshing(false);
    }
  }, [fetchPage, loading, locationId, owner?.accessToken, refreshing]);

  const renderItem = useCallback(
    ({ item, index }: { item: Customer; index: number }) => {
      const phone = String(item?.bp_customer_phone_number || '').trim();
      const hasPhone = phone.length > 0;

      return (
        <Pressable
          unstable_pressDelay={100}
          onPress={() => {
            dispatch(setSelectedCustomerId(String(item?._id || '')));
            navigation.navigate('BackOfficeCustomerDetails', {
              customerId: String(item?._id || ''),
            });
          }}
          style={({ pressed, hovered }: any) => [
            styles.rowCard,
            pressed && styles.rowCardPressed,
            Platform.OS === 'web' && hovered && styles.rowCardHovered,
          ]}>
          <View style={styles.rowNumberColumn}>
            <Text style={styles.rowNumberText}>{index + 1}</Text>
          </View>
          <View style={styles.rowContent}>
            <Text style={styles.rowTitle} numberOfLines={1}>
              {String(item?.bp_customer_name || 'Unnamed customer')}
            </Text>
            <Text
              style={[styles.rowSubtext, hasPhone && styles.phoneLinkText]}
              numberOfLines={1}>
              {hasPhone ? phone : '—'}
            </Text>
          </View>
        </Pressable>
      );
    },
    [dispatch, navigation, styles],
  );

  return (
    <View style={[styles.container, embedded && styles.containerEmbedded]}>
      <View style={styles.listContainer}>
        <FlatList
          data={customers}
          keyExtractor={item => String(item?._id)}
          renderItem={renderItem}
          onEndReached={handleLoadMore}
          onEndReachedThreshold={0.45}
          refreshing={refreshing}
          onRefresh={onRefresh}
          ListHeaderComponent={
            <View style={styles.listHeader}>
              <Text style={styles.listHeaderNumberText}>#</Text>
              <Text style={[styles.listHeaderText, styles.listHeaderNameText]}>
                Name
              </Text>
            </View>
          }
          ListEmptyComponent={
            loading ? (
              <View style={styles.loadingWrap}>
                <ActivityIndicator />
                <Text style={styles.loadingText}>Loading customers...</Text>
              </View>
            ) : (
              <View style={styles.emptyWrap}>
                <MaterialCommunityIcons
                  name="account-outline"
                  size={iconSize(64)}
                  color={theme.colors.textTertiary}
                />
                <Text style={styles.emptyTitle}>No customers found</Text>
                <Text style={styles.emptySubtext}>
                  {debouncedSearch
                    ? 'Try a different search term.'
                    : 'Add a customer name.'}
                </Text>
                {showAdd && !debouncedSearch ? (
                  <Pressable
                    onPress={goAddCustomer}
                    style={({ pressed, hovered }: any) => [
                      styles.emptyCTA,
                      pressed && styles.emptyCTAPressed,
                      Platform.OS === 'web' &&
                      hovered &&
                      styles.emptyCTAHovered,
                    ]}>
                    <Text style={styles.emptyCTAText}>+ Add customer</Text>
                  </Pressable>
                ) : null}
              </View>
            )
          }
          ListFooterComponent={
            isLoadingMore ? (
              <View style={styles.footerLoading}>
                <ActivityIndicator />
              </View>
            ) : null
          }
        />
      </View>
    </View>
  );
};

const createStyles = (theme: any) =>
  StyleSheet.create({
    container: {
      flex: 1,
      padding: s(5),
      backgroundColor: theme.colors.background,
      ...(Platform.OS === 'web' && {
        maxWidth: WEB_MAX_CONTENT_WIDTH,
        alignSelf: 'center',
        width: '100%',
        padding: s(24),
      }),
    },
    containerEmbedded: {
      paddingTop: 0,
    },
    headerCTA: {
      marginRight: s(8),
      paddingHorizontal: s(10),
      paddingVertical: v(6),
      borderRadius: theme.borderRadius.md,
      ...(Platform.OS === 'web' && {
        cursor: 'pointer',
        transition: 'background-color 0.15s ease-out',
      }),
    } as any,
    headerCTAHovered: {
      backgroundColor: 'rgba(255,255,255,0.14)',
    } as any,
    headerCTAPressed: {
      opacity: 0.75,
    },
    headerCTAText: {
      color: theme.colors.textInverse,
      fontFamily: GTFonts.semibold,
      fontSize: fs(15),
      lineHeight: fs(16),
    },
    listContainer: {
      flex: 1,
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.md,
      overflow: 'hidden',
      ...theme.shadows.md,
    },
    listHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingHorizontal: s(12),
      paddingVertical: v(10),
      backgroundColor: theme.colors.surfaceSecondary,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.border,
    },
    listHeaderText: {
      fontFamily: GTFonts.bold,
      fontSize: fs(16),
      lineHeight: fs(18),
      color: theme.colors.text,
    },
    listHeaderNameText: {
      flex: 1,
    },
    listHeaderNumberText: {
      width: s(16),
      marginRight: s(10),
      fontFamily: GTFonts.bold,
      fontSize: fs(16),
      lineHeight: fs(18),
      color: theme.colors.text,
      textAlign: 'center',
    },
    rowCard: {
      flexDirection: 'row',
      alignItems: 'center',
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.border,
      paddingHorizontal: s(12),
      paddingVertical: v(10),
      backgroundColor: theme.colors.surface,
      ...(Platform.OS === 'web' && {
        cursor: 'pointer',
        transition: 'background-color 0.15s ease-out',
      }),
    } as any,
    rowCardHovered: {
      backgroundColor: theme.colors.surfaceSecondary,
    } as any,
    rowCardPressed: {
      opacity: 0.75,
    },
    rowNumberColumn: {
      width: s(16),
      marginRight: s(10),
      alignItems: 'center',
      justifyContent: 'center',
    },
    rowNumberText: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(13),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
      textAlign: 'center',
    },
    rowContent: {
      flex: 1,
      minWidth: 0,
    },
    rowTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(16),
      lineHeight: fs(18),
      color: theme.colors.text,
      marginBottom: v(4),
    },
    rowSubtext: {
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
    },
    phoneLinkText: {
      color: theme.colors.primaryDark,
    },
    loadingWrap: {
      paddingVertical: v(36),
      alignItems: 'center',
    },
    loadingText: {
      marginTop: v(10),
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(18),
      color: theme.colors.textSecondary,
    },
    emptyWrap: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: s(20),
      paddingVertical: v(40),
      gap: v(10),
    },
    emptyTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(16),
      lineHeight: fs(20),
      color: theme.colors.text,
    },
    emptySubtext: {
      textAlign: 'center',
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(18),
      color: theme.colors.textSecondary,
    },
    emptyCTA: {
      marginTop: v(6),
      paddingHorizontal: s(20),
      paddingVertical: v(12),
      borderRadius: theme.borderRadius.md,
      backgroundColor: theme.colors.primary,
      ...(Platform.OS === 'web' && {
        cursor: 'pointer',
        transition: 'opacity 0.15s ease-out',
      }),
    } as any,
    emptyCTAHovered: {
      opacity: 0.92,
    } as any,
    emptyCTAPressed: {
      opacity: 0.88,
    },
    emptyCTAText: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(15),
      lineHeight: fs(18),
      color: theme.colors.textInverse,
    },
    footerLoading: {
      paddingVertical: v(12),
      alignItems: 'center',
    },
  });

export default BackOfficeCustomersScreen;
